import {
  Badge,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { MdGroups, MdOutlineSupportAgent, MdOutlineAssignment } from "react-icons/md";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useTicketModalStore } from "@/store/erp/ticket-soporte/useTicketModalStore";
import { useNavigate } from "react-router-dom";
import { ROUTER_PATHS } from "@/shared/constants";
import { toast } from "react-toastify";
import { useBuscarClientes, useFetchTicketResumen, useImportarClienteDesdeSoftnet, useFetchUsuarioResumen } from "@/shared/actions/erp/tickets-soporte-cliente";
import { useAuthStore } from "@/store/auth/auth.store";
import { useUserModulesStore } from "@/store/auth";
import type { IClienteBusqueda } from "@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface";
import TableTicketSearchByEnter from "./TableTicketSearchByEnter";

// Helpers de identificacion: el sub-flujo "importar desde Softnet" exige
// CI ecuatoriana (10 digitos) o RUC (13 digitos) completos. Tolera
// guiones, espacios y puntos como separadores.
const esIdentificacionValida = (raw: string): boolean => {
  const soloDigitos = (raw || '').replace(/[^0-9]/g, '');
  return soloDigitos.length === 10 || soloDigitos.length === 13;
};
const normalizarIdentificacion = (raw: string): string =>
  (raw || '').replace(/[^0-9]/g, '');


export default function MenuTicketClienteSoporteContrato() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const userModules = useUserModulesStore(s => s.modules);

  // Reglas de visibilidad de los iconos del header:
  //   - "Ver tickets del departamento": se habilita si el usuario es
  //     Supervisor del ERP (comportamiento historico, se preserva) O si es
  //     RESPONSABLE DE AREA en nuestro modulo (Gestion de Accesos -> tab
  //     "Usuarios y Areas", check Responsable). Este segundo camino permite
  //     dar el boton a cualquier usuario (aunque su rol ERP sea Agente) SOLO
  //     con marcarlo responsable, sin depender del rol Supervisor del ERP.
  //     El backend igual filtra los tickets por las areas del usuario.
  //   - "Ver mis tickets": cualquiera con el modulo MIS_TICKETS. Muestra
  //     solo los tickets donde el usuario actual esta como usuario_asignado
  //     (ya filtra asi el backend).
  const rolUsuario = (user?.rol ?? '').toString().toLowerCase();
  const esSupervisor = rolUsuario.includes('supervisor');

  // Responsable de area: viene del resumen del usuario (flag es_responsable_area
  // derivado de api2_usuario_area.es_responsable). Controlado 100% desde
  // Gestion de Accesos.
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _resumen = resumenRes as any;
  const esResponsableArea = !!(
    _resumen?.data?.data?.es_responsable_area ??
    _resumen?.data?.es_responsable_area ??
    _resumen?.es_responsable_area
  );

  const hasTicketsDepartamento =
    (userModules.includes('TICKETS_SOPORTE_CLIENTES.TICKETS_DEPARTAMENTO') && esSupervisor)
    || esResponsableArea;
  const hasMisTickets = userModules.includes('TICKETS_SOPORTE_CLIENTES.MIS_TICKETS');
  const hasAnyTicketModule = userModules.some(m => m.startsWith('TICKETS_SOPORTE_CLIENTES.'));
  // "Ver órdenes de servicio": vista general filtrable (traslados/visitas/etc.).
  // Gateada por el módulo OPERACIONES.ORDENES_SERVICIO_GENERAL (Gestión de Accesos).
  const hasOrdenesServicio = userModules.includes('OPERACIONES.ORDENES_SERVICIO_GENERAL');
  const [cedula, setCedula] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const setIsOpenModal = useTicketModalStore(s => s.setIsModalOpen);
  const setIsModalSearchEnterOpen = useTicketModalStore(s => s.setIsModalSearchEnterOpen);
  const isOpenModalSearchEnter = useTicketModalStore(s => s.isModalSearchEnterOpen);
  const [dataTicketsClientes, setDataTicketsClientes] = useState<IClienteBusqueda[]>([]);
  const lastProcessedQuery = useRef('');

  // Mutation para importar 1 cedula desde Softnet al vuelo cuando la
  // busqueda local devuelve vacio. Reusa POST /clientes/buscar-y-migrar.
  // El componente muestra un Dialog modal mientras la mutation esta en
  // curso (Obteniendo datos del cliente desde Softnet...).
  const importar = useImportarClienteDesdeSoftnet();

  const {
    data: searchResult,
    isLoading: isLoadingSearch,
    isRefetching: isRefetchingSearch,
  } = useBuscarClientes({
    query: searchQuery,
    tipo: 'todos',
    enabled: searchQuery.length >= 3,
  });

  // Badge: tickets PENDIENTES (no en estado final cerrado/cancelado) del
  // departamento del usuario. Usa el campo total_pendientes que devuelve
  // obtenerResumen en el backend. El total absoluto queda en .total
  // por compatibilidad con otros consumers pero NO es lo que mostramos
  // aqui - un ticket cerrado no debe contar como pendiente.
  const { data: resumenDepto } = useFetchTicketResumen({ enabled: hasTicketsDepartamento });
  const totalDepto = (resumenDepto?.data as any)?.total_pendientes
    ?? resumenDepto?.data?.total
    ?? 0;

  // Badge: mis tickets asignados (tambien solo pendientes).
  const { data: resumenMio } = useFetchTicketResumen({
    usuario_asignado: user?.user ? String(user.user) : undefined,
    enabled: !!user?.user && hasMisTickets,
  });
  const totalMio = (resumenMio?.data as any)?.total_pendientes
    ?? resumenMio?.data?.total
    ?? 0;

  // useBuscarClientes devuelve IClienteBusqueda[] directo (no .data anidado).
  const resultados: IClienteBusqueda[] = searchResult?.data || [];

  useEffect(() => {
    if (!searchQuery || isLoadingSearch || isRefetchingSearch) return;
    if (lastProcessedQuery.current === searchQuery) return;

    lastProcessedQuery.current = searchQuery;

    if (resultados.length > 0) {
      setDataTicketsClientes(resultados);
      setIsModalSearchEnterOpen(true);
    } else if (esIdentificacionValida(searchQuery)) {
      // Sub-flujo "importar desde Softnet": no hay resultados en local pero
      // la query es una CI/RUC valida (10 o 13 digitos). Antes pediamos
      // confirmacion al usuario; ahora migramos directo y mostramos el
      // Dialog "Obteniendo datos del cliente desde Softnet..." mientras
      // dura la mutation. El hook invalida ['buscar-clientes'] en onSuccess
      // y los resultados aparecen automaticamente en el modal de busqueda.
      const ident = normalizarIdentificacion(searchQuery);
      // Reseteamos lastProcessedQuery para que cuando lleguen los datos
      // frescos tras el refetch, este mismo useEffect los procese y abra
      // el modal con el cliente recien migrado.
      lastProcessedQuery.current = '';
      importar.mutate(ident);
    } else {
      // Query no es CI/RUC valido (ej: nombre, IP, codigo de contrato).
      // No sugerimos importar desde Softnet porque sin la cedula completa
      // no se puede llamar al endpoint /buscar-y-migrar. El mensaje
      // anterior decia "Ingresa cedula de 10/13 digitos" y confundia al
      // usuario que justamente estaba buscando por contrato o nombre.
      const soloDigitos = searchQuery.replace(/[^0-9]/g, '');
      const pareceIdentificacionIncompleta =
        soloDigitos.length > 0 && soloDigitos.length === searchQuery.trim().length;
      const sugerencia = pareceIdentificacionIncompleta
        ? ' Si lo que buscas es la cedula/RUC, completala (10 o 13 digitos) para importarla desde Softnet.'
        : '';
      toast.error(
        'No se encontraron clientes con ese criterio. Verifica el nombre, CI, IP o numero de contrato.'
          + sugerencia,
        { autoClose: 6000 },
      );
    }

    // Solo limpiamos el INPUT visual (cedula). El searchQuery se mantiene
    // activo mientras el modal esta abierto para que el refetch de
    // ['buscar-clientes'] disparado por mutations de masivo (pausar /
    // reactivar / resolver) traiga datos frescos y los chips MASIVO/ALERTA
    // se actualicen sin recargar la pagina. Cuando el modal cierra
    // (handleCloseModalSearch mas abajo) reseteamos searchQuery.
    setCedula('');
  }, [searchQuery, resultados, isLoadingSearch, isRefetchingSearch]);

  // Mantener dataTicketsClientes sincronizado con los resultados de React
  // Query cuando el modal esta abierto y se refetchea (ej. tras
  // pausar/reactivar/resolver un masivo desde otra pantalla). Asi el
  // chip MASIVO/ALERTA refleja el estado actual del masivo.
  useEffect(() => {
    if (!isOpenModalSearchEnter) return;
    if (!searchQuery || searchQuery.length < 3) return;
    if (isLoadingSearch || isRefetchingSearch) return;
    if (resultados.length === 0) return;
    setDataTicketsClientes(resultados);
  }, [isOpenModalSearchEnter, resultados, isLoadingSearch, isRefetchingSearch]);

  // Cuando el modal cierra reseteamos searchQuery para que la proxima
  // busqueda arranque limpia (sin reusar el query anterior).
  useEffect(() => {
    if (!isOpenModalSearchEnter && searchQuery) {
      setSearchQuery('');
      lastProcessedQuery.current = '';
    }
  }, [isOpenModalSearchEnter]);

  const handleSearchEnter = () => {
    if (!cedula || cedula.length < 3) {
      toast.warning('Ingrese al menos 3 caracteres');
      return;
    }
    lastProcessedQuery.current = '';
    setSearchQuery(cedula);
  };

  const handleSearch = () => {
    setIsOpenModal(true);
  };

  const handleDepartamentoClick = () => {
    navigate(`${ROUTER_PATHS.ticketSoporteCliente.mis_tickets_nav}?tipo=departamento`);
  };

  const handleIndividualClick = () => {
    navigate(`${ROUTER_PATHS.ticketSoporteCliente.mis_tickets_nav}?tipo=individual`);
  };

  const handleOrdenesServicioClick = () => {
    navigate(ROUTER_PATHS.operaciones.ordenesServicioGeneralNav);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Icono de Departamento - solo si tiene el módulo */}
        {hasTicketsDepartamento &&(
          <Tooltip title="Ver tickets del departamento">
            <IconButton
              size="medium"
              onClick={handleDepartamentoClick}
            >
              <Badge
                color="secondary"
                badgeContent={totalDepto}
                max={99}
              >
                <MdGroups />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* Icono Individual - Mis Tickets - solo si tiene el módulo */}
        {hasMisTickets &&(
          <Tooltip title="Ver mis tickets">
            <IconButton
              size="medium"
              onClick={handleIndividualClick}
            >
              <Badge
                color="secondary"
                badgeContent={totalMio}
                max={99}
              >
                <MdOutlineSupportAgent />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* Ver órdenes de servicio (vista general) - solo si tiene el módulo */}
        {hasOrdenesServicio && (
          <Tooltip title="Ver órdenes de servicio">
            <IconButton
              size="medium"
              onClick={handleOrdenesServicioClick}
            >
              <MdOutlineAssignment />
            </IconButton>
          </Tooltip>
        )}

        {/* En MÓVIL: Solo mostrar ícono de búsqueda */}
        {!hasAnyTicketModule ? null : isMobile ? (
          <Tooltip title="Buscar cliente">
            <IconButton onClick={handleSearch} size="medium">
              <SearchIcon color="primary" />
            </IconButton>
          </Tooltip>
        ) : (
          /* En DESKTOP: Mostrar campo completo */
          <>
            <TextField
              size="small"
              placeholder="Buscar cliente (nombre, CI, IP, contrato...)"
              value={cedula}
              onChange={(e) => setCedula(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchEnter();
              }}
              sx={{
                minWidth: 300,
                '& .MuiInputBase-input': {
                  padding: '8.5px 14px',
                  height: 'auto',
                }
              }}
              InputProps={{
                endAdornment: cedula && (
                  <InputAdornment position="end">
                    <Tooltip title="Limpiar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCedula('');
                          setSearchQuery('');
                        }}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              autoComplete="off"
            />

            <Tooltip title="Búsqueda avanzada">
              <IconButton onClick={handleSearch}>
                <SearchIcon color="primary" />
              </IconButton>
            </Tooltip>
          </>
        )}

      </Box>

      {/* Indicador de carga flotante (solo durante busqueda local; la
          importacion desde Softnet ya muestra su propio Dialog modal). */}
      {(isLoadingSearch || isRefetchingSearch) && !importar.isPending && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -32,
            left: 96,
            backgroundColor: 'background.paper',
            px: 2,
            py: 0.75,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 10,
            minWidth: 200,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <CircularProgress size={14} thickness={5} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Buscando clientes...
            </Typography>
          </Stack>
          <LinearProgress sx={{ borderRadius: 1, height: 3 }} />
        </Box>
      )}

      <TableTicketSearchByEnter
        data={dataTicketsClientes}
      />

      {/*
        Dialog modal "Obteniendo datos del cliente desde Softnet...".
        Aparece SOLO mientras la mutation de buscar-y-migrar esta en curso.
        Es no-cerrable (sin boton, sin escape) para evitar que el usuario
        abandone el flujo y dispare otra busqueda mientras se migra.
      */}
      <Dialog
        open={importar.isPending}
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 2, minWidth: 380 } }}
      >
        <DialogContent>
          <Stack spacing={2.5} alignItems="center" sx={{ py: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CloudDownloadIcon color="primary" sx={{ fontSize: 32 }} />
              <CircularProgress size={28} thickness={5} />
            </Stack>
            <Typography variant="h6" textAlign="center" fontWeight={600}>
              Obteniendo datos del cliente desde Softnet...
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Estamos importando el cliente, sus contratos, equipos e historial.
              Esto puede tardar unos segundos. No cierres esta ventana.
            </Typography>
            <LinearProgress sx={{ width: '100%', borderRadius: 1, height: 4 }} />
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
